// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import cloneDeep from "lodash.clonedeep";

import type { IAlarmEntry } from "shared/types/globtypes";
import { IParameter, ILinxParameter, OutputCmd, OutputMode } from "shared/types/parameter";
import { ICloudParameter, IVirtAssign } from "shared/types/cloudtypes";
import { isSet } from "shared/utils/utils";
import {
  ActionType,
  IAction,
  IActionChangeInput,
  IActionChangeOutput,
  IActionConditionBlockStatus,
} from "actions";
import { IState } from "states/states";
import {
  ChargingState,
  IControlDataRow,
  INITIAL_STATE,
  IStateData,
  IStateDataCharger,
  PlugState,
} from "states/datastates";
import { ILiveDataCloud } from "sagas/apisagas";
import { IDataLinx } from "sagas/linxsagas";
import {
  DataType,
  extractArrayData,
  extractDouble,
  extractFloat,
  extractInt,
  parseApiItemArray,
} from "shared/utils/apiutils";
import { id2Index } from "guiutils";
import { IoStateFlag } from "model/output";

const LINX_TASK_RECURRENCE = 1000; // ms LINX task time

export function getSourceIndex(
  selectedSite: number,
  dataSiteId: number | undefined,
  destIndex: number,
  assignmentPara: IVirtAssign[] | undefined,
) {
  // translate indices
  let sourceIndex: number | undefined;
  // look for direct site
  if (!isSet(dataSiteId) || dataSiteId === selectedSite) {
    // we got data of direct site
    // is this index occupied by a virtual site?
    if (
      !assignmentPara?.some(
        // look for matching index, but not if virtual assignment is on original site
        (curAssign) => curAssign.destIndex === destIndex && curAssign.sourceSite !== selectedSite,
      )
    ) {
      // not occupied by virtual assignment
      sourceIndex = destIndex; // no translation needed
    }
  }
  // look for matching virtual parameter
  else {
    // we have virtual parameters; check whether this is one of them
    sourceIndex = assignmentPara?.filter(
      (curAssign) => curAssign.sourceSite === dataSiteId && curAssign.destIndex === destIndex,
    )[0]?.sourceIndex;
  }
  return sourceIndex;
}

function updateControlData(
  state: IStateData,
  parameter: IParameter,
  selectedSite: number,
  data: ILiveDataCloud | IDataLinx,
) {
  const controlData = state.controlcircuit;
  // ensure that we received power live data
  // it is enough to check one single entry of IDataLivePower to ensure all are present
  if (!isSet(data.curTimeOfPeriod)) {
    return controlData;
  }

  // loop through all control circuits
  for (let reg = 0; reg < parameter.regulation.length; reg++) {
    const sourceIndex = getSourceIndex(
      selectedSite,
      data.siteId,
      reg,
      (parameter as ICloudParameter).virtualSite?.regulation, // undefined if ILinxParameter
    );
    if (isSet(sourceIndex)) {
      const curData = isSet(controlData[reg]) ? controlData[reg].values : undefined;
      // determine tariff circuit
      const tlId = parameter.regulation[reg].tariffline;
      const tlIndex = parameter.tariffline.findIndex(
        (curItem, curIndex) => tlId === (curItem.id ?? curIndex),
      );
      // search by id and index (fallback)
      const tariffCircuit = tlIndex >= 0 ? state.tariffcircuit[tlIndex] : undefined;
      const timeOfPeriod = tariffCircuit?.timeOfPeriod ?? 0;
      const energy = tariffCircuit?.energy ?? 0;
      const measuredData = curData?.filter((curItem) => !curItem.isEstimate) ?? [];
      const lastMeasurement: IControlDataRow | undefined =
        measuredData.length ? measuredData[measuredData.length - 1] : undefined;

      // continue old data (by removing estimates) or start new series?
      const dataSeries =
        lastMeasurement && lastMeasurement.timestamp < timeOfPeriod ?
          [...controlData[reg].values.filter((curRow) => !curRow.isEstimate)]
        : [];

      // append current data set
      // is this an estimate or a new energy meter pulse?
      const isEstimate = isSet(lastMeasurement) && lastMeasurement.energy === energy;
      let newEnergy: number;
      if (isEstimate) {
        newEnergy =
          lastMeasurement.energy +
          ((tariffCircuit?.power ?? 0) * (timeOfPeriod - lastMeasurement.timestamp)) / 3600.0;
      } else {
        // real measurement
        newEnergy = energy;
      }
      dataSeries.push({
        timestamp: timeOfPeriod,
        energy: newEnergy,
        isEstimate,
      });

      // assign new data
      controlData[reg] = {
        // these values will be filled later/outside
        targetValue: extractFloat(data.curTargetValues!, sourceIndex, true),
        regulationPriorityStatus: extractInt(
          data.curRegulationPriorityStatus!,
          1,
          sourceIndex,
          true,
        ),
        trendLevel: extractInt(data.curTrendLevel!, 1, sourceIndex, true),
        values: dataSeries,
      };
    }
  }
  return controlData;
}

// only needed for local webinterface of enline controller
function updateChargerData(
  state: IStateDataCharger[] | undefined,
  data: ILiveDataCloud | IDataLinx,
  parameter: ILinxParameter | ICloudParameter,
  selectedSite: number,
  isDirect: boolean, // controller-direct (LINX/proxy) vs cloud push encoding
) {
  // ensure that we received charger live data
  // it is enough to check one single entry of IDataLiveCharger to ensure all are present
  if (!isSet(data.chargerCurP)) {
    return state;
  }
  // ensure state array
  if (!Array.isArray(state)) {
    state = [];
  }

  // start with existing charger data
  for (let curCharger = 0; curCharger < parameter.charger.length; curCharger++) {
    // check whether we are listening to this charger
    const sourceIndex = getSourceIndex(
      selectedSite,
      data.siteId,
      curCharger,
      (parameter as ICloudParameter).virtualSite?.charger, // undefined if ILinxParameter
    );
    if (!isSet(sourceIndex)) {
      // data for this charger are not present in the provided dataset
      continue;
    }

    // request status
    // take care: the output index is those of the source site!
    const dpoReqMaxPowerIndex = id2Index(
      parameter.condition,
      parameter.charger[curCharger].dpoReqMaxPower,
    );
    const reqMaxPower =
      isSet(dpoReqMaxPowerIndex) && isSet(data.conditionStatus) ?
        isDirect ? extractDouble(data.conditionStatus, dpoReqMaxPowerIndex, true)
        : extractFloat(data.conditionStatus, dpoReqMaxPowerIndex, true)
      : undefined;
    const dpoReqMaxCurrentIndex = id2Index(
      parameter.condition,
      parameter.charger[curCharger].dpoReqMaxCurrent,
    );
    const reqMaxCurrent =
      isSet(dpoReqMaxCurrentIndex) && isSet(data.conditionStatus) ?
        isDirect ? extractDouble(data.conditionStatus, dpoReqMaxCurrentIndex, true)
        : extractFloat(data.conditionStatus, dpoReqMaxCurrentIndex, true)
      : undefined;
    // extract phase data
    const curI: number[] = [];
    const curU: number[] = [];
    for (let phase = 0; phase < 3; phase++) {
      curI.push(extractFloat(data.chargerCurI!, sourceIndex * 3 + phase, true));
      curU.push(extractFloat(data.chargerCurU!, sourceIndex * 3 + phase, true));
    }
    // extract state data
    const chargerState = extractInt(data.chargerState!, 4, sourceIndex, true);
    const plugState =
      (chargerState & 0xff) !== 0xff ? ((chargerState & 0xff) as PlugState) : undefined;
    const plugIsLocked =
      ((chargerState >> 8) & 0xff) !== 0xff ? ((chargerState >> 8) & 0xff) !== 0 : undefined;
    const chargingState =
      ((chargerState >> 16) & 0xff) !== 0xff ?
        (((chargerState >> 16) & 0xff) as ChargingState)
      : undefined;
    const isConnected = (chargerState & (1 << 24)) !== 0;
    const chargerWritePending = (chargerState & (1 << 25)) !== 0;
    const errorChargerRW = (chargerState & (0b11 << 26)) !== 0;
    const curP = extractFloat(data.chargerCurP, sourceIndex, true);
    const devMaxI = extractFloat(data.chargerDevMaxI!, sourceIndex, true);
    const setMaxI = extractFloat(data.chargerSetMaxI!, sourceIndex, true);
    const eTotal =
      isDirect ?
        extractDouble(data.chargerETotal!, sourceIndex, true)
      : extractFloat(data.chargerETotal!, sourceIndex, true);
    const eSession = extractFloat(data.chargerESession!, sourceIndex, true);

    // put everything together
    const chargerData: IStateDataCharger = {
      curP: !isNaN(curP) ? curP : undefined,
      curI: curI.some((val) => !isNaN(val)) ? curI : undefined,
      curU: curU.some((val) => !isNaN(val)) ? curU : undefined,
      devMaxI: !isNaN(devMaxI) ? devMaxI : undefined,
      setMaxI: !isNaN(setMaxI) ? setMaxI : undefined,
      eTotal: !isNaN(eTotal) ? eTotal : undefined,
      eSession: !isNaN(eSession) ? eSession : undefined,
      plugState,
      plugIsLocked,
      chargingState,
      isConnected,
      chargerWritePending,
      errorChargerRW,
      reqMaxPower,
      reqMaxCurrent,
    };
    state[curCharger] = chargerData;
  }
  return state;
}

function parseNewLiveData(
  state: IStateData,
  data: ILiveDataCloud | IDataLinx,
  rootState: IState,
  isDirect: boolean, // controller-direct (LINX/proxy) vs cloud push encoding
): IStateData {
  // we might have just disconnected
  if (!rootState.para.parameter || !isSet(rootState.cached.selectedSite)) {
    return state;
  }
  // ensure that we received power live data
  // it is enough to check one single entry of IDataLivePower to ensure all are present
  if (!isSet(data.curTimeOfPeriod)) {
    return state;
  }

  const selectedSite = rootState.cached.selectedSite;
  const virtualSite = (rootState.para.parameter as ICloudParameter).virtualSite; // undefined if ILinxParameter
  state.timestamp = data.systemTime; // for virtual sites, timestamp gets repeatedly overwritten

  // extract input data
  for (let i = 0; i < rootState.para.parameter.input.length; i++) {
    const sourceIndex = getSourceIndex(selectedSite, data.siteId, i, virtualSite?.input);
    if (isSet(sourceIndex)) {
      if (isDirect) {
        state.input[i] = extractDouble(data.inputStatus!, sourceIndex, true);
      } else {
        state.input[i] = extractFloat(data.inputStatus!, sourceIndex, true);
      }
    }
  }

  // we ignore setting the output data if we made a write request just before
  // because the LINX task may not be run yet and the output status still the old value
  if (
    !rootState.scheduler.setOutput.lastRequest ||
    Date.now() - rootState.scheduler.setOutput.lastRequest > 3 * LINX_TASK_RECURRENCE
  ) {
    for (let i = 0; i < rootState.para.parameter.output.length; i++) {
      const sourceIndex = getSourceIndex(selectedSite, data.siteId, i, virtualSite?.output);
      const outPara = rootState.para.parameter.output[i];
      if (isSet(sourceIndex)) {
        let logical: number;
        let physical: number;
        let outState: number | undefined;
        if (!isDirect) {
          logical = extractFloat((data as ILiveDataCloud).outputStatus!, sourceIndex, true);
          if (![OutputMode.ANALOG, OutputMode.PWM].includes(outPara.mode)) {
            logical = logical & IoStateFlag.LINX_REQ_STATE ? 1 : 0; // bit-coded value
          }
          physical = extractFloat(
            (data as ILiveDataCloud).outputStatusPhysical!,
            sourceIndex,
            true,
          );
          outState =
            [OutputMode.ANALOG, OutputMode.PWM].includes(outPara.mode) ?
              logical ? IoStateFlag.LINX_REQ_STATE
              : 0
            : extractFloat((data as ILiveDataCloud).outputStatus!, sourceIndex, true);
        } else {
          logical = extractDouble((data as IDataLinx).outputValue, sourceIndex, true);
          physical = extractDouble((data as IDataLinx).outputValuePhysical, sourceIndex, true);
          outState = extractInt((data as IDataLinx).outputState, 1, sourceIndex, true);
        }
        state.output[i] = { state: outState, logical, physical };
      }
    }
  }

  // extract tariff circuit data
  for (let i = 0; i < rootState.para.parameter.tariffline.length; i++) {
    const sourceIndex = getSourceIndex(selectedSite, data.siteId, i, virtualSite?.tariffline);
    if (isSet(sourceIndex)) {
      const tlPara = rootState.para.parameter.tariffline[i];
      const timeOfPeriod = extractInt(data.curTimeOfPeriod, 4, sourceIndex, true);
      const power = extractFloat(data.curPower!, sourceIndex, true);
      let energy =
        isSet(data.accEnergy) ? extractFloat(data.accEnergy, sourceIndex, true) : undefined;
      if (!isSet(energy)) {
        const accPower = extractFloat((data as ILiveDataCloud).curEnergy ?? "", sourceIndex, true);
        // we need to convert it to energy unit
        energy = (accPower * tlPara.regulationPeriod) / 3600.0; // convert to energy unit
      }
      console.assert(isSet(energy), "missing energy in live data.", data);
      state.tariffcircuit[i] = {
        timeOfPeriod,
        power,
        energy: energy ?? 0,
      };
    }
  }
  // energy meter data
  for (let i = 0; i < rootState.para.parameter.energymeter.length; i++) {
    const sourceIndex = getSourceIndex(selectedSite, data.siteId, i, virtualSite?.energymeter);
    if (isSet(sourceIndex)) {
      state.energymeter[i] = extractDouble(data.energyMeterValues!, sourceIndex, true);
    }
  }
  // control circuit data
  state.controlcircuit = updateControlData(state, rootState.para.parameter, selectedSite, data);

  // conditions and modules
  state.condition = extractArrayData(
    data.conditionStatus ?? "",
    isDirect ? DataType.DOUBLE : DataType.FLOAT,
    rootState.para.parameter.condition.length,
  );
  state.module =
    isSet(data.moduleStatus) ?
      extractArrayData(
        data.moduleStatus ?? "",
        DataType.BYTE,
        rootState.para.parameter.module.length,
      ).map((curItem) => Boolean(curItem))
    : [];

  if (hasChargerData(data)) {
    state.charger = updateChargerData(
      state.charger,
      data,
      rootState.para.parameter,
      selectedSite,
      isDirect,
    );
  }
  return state;
}

export function hasChargerData(data: ILiveDataCloud | IDataLinx): boolean {
  if (
    data.chargerCurP ||
    data.chargerDevMaxI ||
    data.chargerESession ||
    data.chargerETotal ||
    data.chargerSetMaxI ||
    data.chargerAuth ||
    data.chargerState
  ) {
    return true;
  }
  return false;
}

export default function dataReducer(
  state: IStateData,
  action: IAction<unknown>,
  rootState: IState,
): IStateData {
  // type convert action string back into ActionType
  switch (action.type) {
    case ActionType.ACT_DISCONNECT_SITE:
      state = cloneDeep(INITIAL_STATE);
      return state;

    case ActionType.ACT_GET_ALLDATA_SUCC: {
      // controller-direct live data (LINX or via proxy)
      const data = action.payload as IDataLinx;
      state = parseNewLiveData(state, data, rootState, true);
      return state;
    }
    case ActionType.ACT_GET_CLOUDLIVEDATA_SUCC: {
      // live data received via the GraphQL push subscription
      const data = action.payload as ILiveDataCloud;
      state = parseNewLiveData(state, data, rootState, false);
      return state;
    }

    case ActionType.ACT_GET_CONDITIONBLOCKSTATUS_SUCC: {
      const data = action.payload as IActionConditionBlockStatus;
      state.conditionBlockStatus = {
        conditionIndex: data.conditionIndex,
        blockResult: extractArrayData(data.blockResult, DataType.DOUBLE),
        dataValue: extractArrayData(data.dataValue, DataType.DOUBLE),
        connectionStatus: extractArrayData(data.connectionStatus, DataType.DOUBLE),
      };
      return state;
    }

    case ActionType.ACT_GET_ALARMS_SUCC: {
      const data = action.payload as IAlarmEntry[];
      state.alarms = parseApiItemArray(data);
      return state;
    }
    case ActionType.ACT_APPEND_CLOUDALARM: {
      const data = action.payload as IAlarmEntry;
      // do we need to update an existing alarm?
      const existingItem = state.alarms.filter(
        (curItem) =>
          curItem.alarmTimestamp === data.alarmTimestamp &&
          curItem.source === data.source &&
          curItem.value === data.value,
      );
      if (existingItem.length === 1) {
        // update existing alarm
        existingItem[0].state = data.state;
        existingItem[0].clearTimestamp = data.clearTimestamp;
      } else {
        // add new item
        console.assert(!isSet(data.clearTimestamp), "New cleared alarm?", data, state.alarms);
        state.alarms.push(data);
      }
      return state;
    }

    case ActionType.ACT_CHANGE_INPUT_SUCC: {
      const request = action.payload as IActionChangeInput;
      // update local input data
      state.input[request.index] = request.value;
      return state;
    }
    case ActionType.ACT_CHANGE_OUTPUT_SUCC: {
      const request = action.payload as IActionChangeOutput;
      // update local output data
      if (request.cmd === OutputCmd.OVERRIDE_OFF) {
        state.output[request.index].physical = 0;
      } else if (request.cmd === OutputCmd.OVERRIDE_ON) {
        state.output[request.index].physical = 1;
      } else if (request.cmd === OutputCmd.OFF) {
        state.output[request.index].logical = 0;
        const invert = rootState.para.parameter?.output[request.index]?.mode === OutputMode.OPEN;
        state.output[request.index].physical = invert ? 1 : 0;
      } else if (request.cmd === OutputCmd.ON) {
        state.output[request.index].logical = 1;
        const invert = rootState.para.parameter?.output[request.index]?.mode === OutputMode.OPEN;
        state.output[request.index].physical = invert ? 0 : 1;
      } else if (request.cmd === OutputCmd.TOGGLE) {
        state.output[request.index].logical = state.output[request.index].logical ? 0 : 1;
        state.output[request.index].physical = state.output[request.index].physical ? 0 : 1;
      }
      return state;
    }

    default:
      return state;
  }
}
